import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import Select from "react-select";
import "./AddCounterparty.css";
import { selectStyles } from "../../../../utils/selectStyles";
import {
  updateLegalEntityCounterparty,
  updatePhysicalEntityCounterparty,
  getLegalEntityCounterparty,
  getPhysicalEntityCounterparty,
  getCounterpartyBankAccounts,
  deleteCounterparty,
  createPhysicalEntityCounterparty,
  createLegalEntityCounterparty,
  addCounterpartyBankAccount,
  updateCounterpartyBankAccount,
} from "../../../../api/api";

const AddCounterparty = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const isEditMode = !!id;

  // Определяем тип из URL или по умолчанию counterparty
  const searchParams = new URLSearchParams(location.search);
  const initialEntityType = searchParams.get("type") || "counterparty";
  const entityKind = searchParams.get("entity") || "legal_entity";

  // Тип контрагента - синхронизирован с URL
  const [counterpartyType, setCounterpartyType] = useState(
    initialEntityType === "organization" ? "organization" : "counterparty"
  );

  // Определяем isOrganization на основе counterpartyType
  const isOrganization = counterpartyType === "organization";

  const [loading, setLoading] = useState(isEditMode);

  // Обновляем URL при изменении типа контрагента
  useEffect(() => {
    const newType =
      counterpartyType === "organization" ? "organization" : "counterparty";
    const currentParams = new URLSearchParams(location.search);
    currentParams.set("type", newType);

    // Обновляем URL без перезагрузки страницы
    const newUrl = `${location.pathname}?${currentParams.toString()}`;
    window.history.replaceState({}, "", newUrl);
  }, [counterpartyType, location.pathname, location.search]);

  // Основная информация
  const [legalForm, setLegalForm] = useState(null);
  const [binIin, setBinIin] = useState("");
  const [fullName, setFullName] = useState("");
  const [shortName, setShortName] = useState("");

  // Для физических лиц
  const [personFullName, setPersonFullName] = useState("");

  // Документ, удостоверяющий личность
  const [documentType, setDocumentType] = useState(null);
  const [documentNumber, setDocumentNumber] = useState("");
  const [documentIssueDate, setDocumentIssueDate] = useState("");
  const [documentExpirationDate, setDocumentExpirationDate] = useState("");
  const [documentIssuedBy, setDocumentIssuedBy] = useState("");

  // Контактная информация для физических лиц
  const [personPhone, setPersonPhone] = useState("");
  const [personEmail, setPersonEmail] = useState("");

  // Адреса
  const [legalCountry, setLegalCountry] = useState({
    value: "KZ",
    label: "Казахстан",
  });
  const [legalCity, setLegalCity] = useState(null);
  const [legalBuilding, setLegalBuilding] = useState("");
  const [legalPostalCode, setLegalPostalCode] = useState("");
  const [legalStreet, setLegalStreet] = useState("");
  const [actualAddressMatches, setActualAddressMatches] = useState(true);
  const [actualCountry, setActualCountry] = useState({
    value: "KZ",
    label: "Казахстан",
  });
  const [actualCity, setActualCity] = useState(null);
  const [actualBuilding, setActualBuilding] = useState("");
  const [actualPostalCode, setActualPostalCode] = useState("");
  const [actualStreet, setActualStreet] = useState("");

  // Руководство и контакты
  const [directorFullName, setDirectorFullName] = useState("");
  const [directorPosition, setDirectorPosition] = useState("");
  const [organizationPhone, setOrganizationPhone] = useState("");
  const [organizationEmail, setOrganizationEmail] = useState("");
  const [website, setWebsite] = useState("");

  // Контактные лица
  const [contactPersons, setContactPersons] = useState([]);

  // Лицензии
  const [hasLicenses, setHasLicenses] = useState(false);
  const [licenseNumber, setLicenseNumber] = useState("");
  const [licenseIssueDate, setLicenseIssueDate] = useState("");
  const [licenseExpirationDate, setLicenseExpirationDate] = useState("");

  // Налоги
  const [taxRegime, setTaxRegime] = useState(null);
  const [isNdsPayer, setIsNdsPayer] = useState(true);

  // Банковские счета
  const [bankAccounts, setBankAccounts] = useState([]);

  // Валидация
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);

  // Моковые данные для выпадающих списков
  const legalFormOptions = [
    { value: "ТОО", label: "ТОО" },
    { value: "ИП", label: "ИП" },
    { value: "Физическое лицо на ГПХ", label: "Физическое лицо на ГПХ" },
  ];

  const documentTypeOptions = [
    { value: "Удостоверение личности", label: "Удостоверение личности" },
    { value: "Паспорт", label: "Паспорт" },
  ];

  // Проверка, является ли выбранная форма физическим лицом (ИП и ГПХ — одна и та же форма, как у физлица)
  const isIndividualPerson =
    legalForm?.value === "ИП" ||
    legalForm?.value?.includes("Физическое лицо");

  const cityOptions = [
    { value: "almaty", label: "Алматы" },
    { value: "astana", label: "Астана" },
    { value: "shymkent", label: "Шымкент" },
  ];

  const taxRegimeOptions = [
    { value: "ОСН", label: "ОСН" },
    { value: "УСН", label: "УСН" },
  ];

  // Функция для разбора адреса из строки
  const parseAddress = (addressStr) => {
    if (!addressStr)
      return {
        country: { value: "KZ", label: "Казахстан" },
        city: null,
        street: "",
        building: "",
        postalCode: "",
      };

    const parts = addressStr.split(",").map((p) => p.trim());
    let country = { value: "KZ", label: "Казахстан" };
    let postalCode = "";
    let city = null;
    let street = "";
    let building = "";

    // Пытаемся найти страну
    if (parts[0] === "Казахстан") {
      country = { value: "KZ", label: "Казахстан" };
      postalCode = parts[1] || "";
      city = cityOptions.find((c) => c.label === parts[2]) || null;
      street = parts.slice(3).join(", ") || "";
    } else {
      // Если первый элемент не страна, пытаемся найти индекс
      if (parts[0]?.match(/^\d+$/)) {
        postalCode = parts[0];
        city = cityOptions.find((c) => c.label === parts[1]) || null;
        street = parts.slice(2).join(", ") || "";
      } else {
        city = cityOptions.find((c) => c.label === parts[0]) || null;
        street = parts.slice(1).join(", ") || "";
      }
    }

    // Разделяем улицу и здание, если есть
    if (street) {
      const streetParts = street.match(/^(.+?)\s*,?\s*(\d+)$/);
      if (streetParts) {
        street = streetParts[1].trim();
        building = streetParts[2];
      }
    }

    return { country, city, street, building, postalCode };
  };

  /** Приводит ответ API контрагентов к формату формы (legal_address, bin_iin, full_name и т.д.) */
  const normalizeCounterpartyForForm = (data) => {
    if (!data) return null;
    const parts = [
      data.country,
      data.postal_code,
      data.city,
      data.street,
      data.building,
    ].filter(Boolean);
    const legalAddressStr = parts.length > 0 ? parts.join(", ") : null;
    return {
      ...data,
      bin_iin: data.bin_or_inn ?? data.bin_iin,
      full_name: data.full_company_name ?? data.full_name,
      short_name: data.short_company_name ?? data.short_name ?? data.full_name,
      director_full_name:
        data.full_name_first_supervisor ?? data.director_full_name,
      contact_person_email: data.email ?? data.contact_person_email,
      legal_address: data.legal_address ?? legalAddressStr,
      actual_address: data.is_actual_equals_legal
        ? data.legal_address ?? legalAddressStr
        : data.actual_address ?? null,
    };
  };

  // Загрузка данных для редактирования
  useEffect(() => {
    const loadCounterpartyData = async () => {
      if (!isEditMode || !id) return;

      try {
        setLoading(true);
        const fetchData =
          entityKind === "physical_entity"
            ? getPhysicalEntityCounterparty(id)
            : getLegalEntityCounterparty(id);
        const rawData = await fetchData;
        const data = normalizeCounterpartyForForm(rawData);

        // Заполняем форму данными
        if (data.legal_form) {
          const formOption = legalFormOptions.find(
            (opt) => opt.value === data.legal_form
          );
          setLegalForm(formOption || null);
        }

        setBinIin(data.bin_iin || "");
        setFullName(data.full_name || "");
        setShortName(data.short_name || "");

        // Разбираем адреса
        if (data.legal_address) {
          const legalAddr = parseAddress(data.legal_address);
          setLegalCountry(legalAddr.country);
          setLegalCity(legalAddr.city);
          setLegalStreet(legalAddr.street);
          setLegalBuilding(legalAddr.building);
          setLegalPostalCode(legalAddr.postalCode);
        }

        if (data.actual_address) {
          const actualAddr = parseAddress(data.actual_address);
          const legalAddr = parseAddress(data.legal_address || "");
          // Проверяем, совпадают ли адреса
          const addressesMatch = data.actual_address === data.legal_address;
          setActualAddressMatches(addressesMatch);

          if (!addressesMatch) {
            setActualCountry(actualAddr.country);
            setActualCity(actualAddr.city);
            setActualStreet(actualAddr.street);
            setActualBuilding(actualAddr.building);
            setActualPostalCode(actualAddr.postalCode);
          } else {
            setActualCountry(legalAddr.country);
            setActualCity(legalAddr.city);
            setActualStreet(legalAddr.street);
            setActualBuilding(legalAddr.building);
            setActualPostalCode(legalAddr.postalCode);
          }
        }

        setDirectorFullName(data.director_full_name || "");
        setOrganizationPhone(data.phone || "");
        setOrganizationEmail(data.contact_person_email || "");

        if (rawData.type === "physical_entity") {
          setPersonFullName(rawData.full_name || "");
          const docTypeOpt = documentTypeOptions.find(
            (o) => o.value === rawData.document_type
          );
          setDocumentType(docTypeOpt || null);
          setDocumentNumber(rawData.document_number || "");
          setDocumentIssueDate(rawData.document_date_of_issue || "");
          setDocumentExpirationDate(rawData.document_validity_period || "");
          setDocumentIssuedBy(rawData.document_issued_by || "");
          setPersonPhone(rawData.phone || "");
          setPersonEmail(rawData.email || "");
        }

        // Контактные лица
        if (data.contact_person) {
          setContactPersons([
            {
              id: 1,
              fullName: data.contact_person || "",
              phone: data.contact_person_phone || "",
              email: data.contact_person_email || "",
              role: "",
            },
          ]);
        }

        // Лицензии
        if (data.license_number) {
          setHasLicenses(true);
          setLicenseNumber(data.license_number || "");
          setLicenseIssueDate(data.license_issue_date || "");
          setLicenseExpirationDate(data.license_expiration_date || "");
        }

        // Налоги
        if (data.tax_regime) {
          const taxOption = taxRegimeOptions.find(
            (opt) => opt.value === data.tax_regime
          );
          setTaxRegime(taxOption || null);
        }
        setIsNdsPayer(
          data.is_nds_payer !== undefined ? data.is_nds_payer : true
        );

        // Банковские счета: загрузка GET /partners/counterparties/bank_account/all/{counterparty_id}
        try {
          const counterpartyId = parseInt(id, 10);
          if (!Number.isNaN(counterpartyId)) {
            const accounts = await getCounterpartyBankAccounts(counterpartyId);
            const list = Array.isArray(accounts) ? accounts : [];
            const mapped = list.map((acc) => ({
              id: acc.id,
              bankName: acc.bank_name || "",
              accountNumber: acc.iik || "",
              bik: acc.bik || "",
              currency: acc.currency || "",
              isMain: !!acc.is_primary,
            }));
            const hasPrimary = mapped.some((a) => a.isMain);
            if (mapped.length > 0 && !hasPrimary) mapped[0].isMain = true;
            setBankAccounts(mapped);
          } else {
            setBankAccounts([]);
          }
        } catch (bankErr) {
          console.error("Ошибка при загрузке банковских счетов:", bankErr);
          setBankAccounts([]);
        }
      } catch (error) {
        console.error("Ошибка при загрузке данных контрагента:", error);
        setSubmitError("Не удалось загрузить данные контрагента");
      } finally {
        setLoading(false);
      }
    };

    loadCounterpartyData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, id, isOrganization, entityKind]);

  const handleAddContactPerson = () => {
    setContactPersons([
      ...contactPersons,
      {
        id: Date.now(),
        fullName: "",
        position: "",
        phone: "",
        email: "",
      },
    ]);
  };

  const handleRemoveContactPerson = (id) => {
    setContactPersons(contactPersons.filter((person) => person.id !== id));
  };

  const handleContactPersonChange = (id, field, value) => {
    setContactPersons(
      contactPersons.map((person) =>
        person.id === id ? { ...person, [field]: value } : person
      )
    );
  };

  const handleAddBankAccount = () => {
    setBankAccounts([
      ...bankAccounts,
      {
        id: Date.now(),
        bankName: "",
        accountNumber: "",
        bik: "",
        currency: "",
        isMain: bankAccounts.length === 0, // Первый счет по умолчанию основной
      },
    ]);
  };

  const handleRemoveBankAccount = (id) => {
    const accountToRemove = bankAccounts.find((acc) => acc.id === id);
    const newAccounts = bankAccounts.filter((account) => account.id !== id);

    // Если удаляемый счет был основным и остались другие счета, делаем первый основной
    if (accountToRemove?.isMain && newAccounts.length > 0) {
      newAccounts[0].isMain = true;
    }

    setBankAccounts(newAccounts);
  };

  const handleBankAccountChange = (id, field, value) => {
    setBankAccounts(
      bankAccounts.map((account) =>
        account.id === id ? { ...account, [field]: value } : account
      )
    );
  };

  const handleMainAccountChange = (id, isMain) => {
    if (isMain) {
      // Если выбираем этот счет как основной, снимаем флаг с остальных
      setBankAccounts(
        bankAccounts.map((account) =>
          account.id === id
            ? { ...account, isMain: true }
            : { ...account, isMain: false }
        )
      );
    } else {
      // Если снимаем флаг основного, просто обновляем этот счет
      setBankAccounts(
        bankAccounts.map((account) =>
          account.id === id ? { ...account, isMain: false } : account
        )
      );
    }
  };

  const handleActualAddressToggle = (checked) => {
    setActualAddressMatches(checked);
    if (checked) {
      setActualCountry(legalCountry);
      setActualCity(legalCity);
      setActualBuilding(legalBuilding);
      setActualPostalCode(legalPostalCode);
      setActualStreet(legalStreet);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!legalForm)
      newErrors.legalForm = "Выберите организационно-правовую форму";
    if (!binIin || binIin.length !== 12)
      newErrors.binIin = "Введите корректный БИН/ИИН (12 цифр)";

    if (isIndividualPerson) {
      if (!personFullName.trim()) newErrors.personFullName = "Введите ФИО";
    } else {
      if (!fullName.trim()) newErrors.fullName = "Введите полное наименование";
      if (!shortName.trim())
        newErrors.shortName = "Введите сокращенное наименование";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Функция для форматирования адреса в строку
  const formatAddress = (country, city, street, building, postalCode) => {
    const parts = [];
    if (country?.label) parts.push(country.label);
    if (postalCode) parts.push(postalCode);
    if (city?.label) parts.push(city.label);
    if (street) parts.push(street);
    if (building) parts.push(building);
    return parts.join(", ") || "";
  };

  // Преобразование пустых строк в null перед отправкой на бэк
  const normalizeEmptyStringsToNull = (obj) => {
    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => [
        key,
        typeof value === "string" && value.trim() === "" ? null : value,
      ])
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Формируем адреса
      const legalAddressStr = formatAddress(
        legalCountry,
        legalCity,
        legalStreet,
        legalBuilding,
        legalPostalCode
      );

      const actualAddressStr = actualAddressMatches
        ? legalAddressStr
        : formatAddress(
            actualCountry,
            actualCity,
            actualStreet,
            actualBuilding,
            actualPostalCode
          );

      // Получаем данные первого контактного лица (если есть)
      const firstContactPerson =
        contactPersons.length > 0 ? contactPersons[0] : null;

      if (isEditMode) {
        // Редактирование: формат API контрагентов (legal_entity / physical_entity)
        const countryStr =
          legalCountry?.label || legalCountry?.value || null;
        const cityStr = legalCity?.label ?? legalCity?.value ?? null;
        const commonFields = normalizeEmptyStringsToNull({
          is_nds_payer: isNdsPayer,
          side_type: isOrganization ? "our_company" : "counterparty",
          legal_form: legalForm?.value || null,
          bin_or_inn: binIin || null,
          country: countryStr,
          city: cityStr,
          street: legalStreet || null,
          building: legalBuilding || null,
          postal_code: legalPostalCode || null,
          is_actual_equals_legal: actualAddressMatches,
          phone: (isIndividualPerson ? personPhone : organizationPhone) || null,
          email: (isIndividualPerson ? personEmail : organizationEmail) || null,
        });

        if (entityKind === "physical_entity") {
          const physicalBody = {
            ...commonFields,
            full_name: personFullName || null,
            document_type: documentType?.value ?? null,
            document_number: documentNumber || null,
            document_date_of_issue: documentIssueDate || null,
            document_validity_period: documentExpirationDate || null,
            document_issued_by: documentIssuedBy || null,
          };
          const result = await updatePhysicalEntityCounterparty(
            id,
            normalizeEmptyStringsToNull(physicalBody)
          );
          console.log("Контрагент (физ. лицо) успешно обновлен:", result);
        } else {
          const legalBody = {
            ...commonFields,
            full_company_name: fullName || null,
            short_company_name: shortName || null,
            full_name_first_supervisor: directorFullName || null,
            job_title: directorPosition || null,
          };
          const result = await updateLegalEntityCounterparty(
            id,
            normalizeEmptyStringsToNull(legalBody)
          );
          console.log(
            isOrganization ? "Наша компания обновлена:" : "Контрагент (юр. лицо) успешно обновлен:",
            result
          );
        }

        // Банковские счета: обновление существующих (с id) или добавление новых
        if (bankAccounts.length > 0) {
          for (const account of bankAccounts) {
            const bankName = account.bankName?.trim();
            const iik = account.accountNumber?.trim();
            const bik = account.bik?.trim();
            const currency = account.currency?.trim();
            if (!bankName || !iik || !bik || !currency) continue;
            if (account.id) {
              await updateCounterpartyBankAccount(account.id, {
                counterparty_id: parseInt(id, 10),
                bank_name: bankName,
                bik,
                iik,
                currency,
                is_primary: !!account.isMain,
                is_active: true,
              });
            } else {
              await addCounterpartyBankAccount(id, {
                bank_name: bankName,
                bik,
                iik,
                currency,
                is_primary: !!account.isMain,
                is_active: true,
              });
            }
          }
        }
      } else {
        // Создание: и контрагент, и наша компания — через API контрагентов (side_type: counterparty | our_company)
        const isLegalEntityForm = legalForm?.value === "ТОО";
        const isPhysicalEntityForm =
          legalForm?.value === "ИП" ||
          legalForm?.value === "Физическое лицо на ГПХ";

        const countryStr =
          legalCountry?.label || legalCountry?.value || null;
        const cityStr = legalCity?.label ?? legalCity?.value ?? null;

        const commonFields = normalizeEmptyStringsToNull({
          is_nds_payer: isNdsPayer,
          side_type: isOrganization ? "our_company" : "counterparty",
          legal_form: legalForm?.value || null,
          bin_or_inn: binIin || null,
          country: countryStr,
          city: cityStr,
          street: legalStreet || null,
          building: legalBuilding || null,
          postal_code: legalPostalCode || null,
          is_actual_equals_legal: actualAddressMatches,
          phone: (isIndividualPerson ? personPhone : organizationPhone) || null,
          email: (isIndividualPerson ? personEmail : organizationEmail) || null,
        });

        let result;

        if (isLegalEntityForm) {
          const legalBody = {
            ...commonFields,
            type: "legal_entity",
            full_company_name: fullName || null,
            short_company_name: shortName || null,
            full_name_first_supervisor: directorFullName || null,
            job_title: directorPosition || null,
          };
          console.log(
            isOrganization ? "Отправка нашей компании (юр. лицо):" : "Отправка юр. лица контрагента:",
            legalBody
          );
          result = await createLegalEntityCounterparty(
            normalizeEmptyStringsToNull(legalBody)
          );
          console.log(
            isOrganization ? "Наша компания (юр. лицо) успешно создана:" : "Контрагент (юр. лицо) успешно создан:",
            result
          );
        } else if (isPhysicalEntityForm) {
          const physicalBody = {
            ...commonFields,
            type: "physical_entity",
            full_name: personFullName || null,
            document_type: documentType?.value ?? null,
            document_number: documentNumber || null,
            document_date_of_issue: documentIssueDate || null,
            document_validity_period: documentExpirationDate || null,
            document_issued_by: documentIssuedBy || null,
          };
          console.log(
            isOrganization ? "Отправка нашей компании (физ. лицо):" : "Отправка физ. лица контрагента:",
            physicalBody
          );
          result = await createPhysicalEntityCounterparty(
            normalizeEmptyStringsToNull(physicalBody)
          );
          console.log(
            isOrganization ? "Наша компания (физ. лицо) успешно создана:" : "Контрагент (физ. лицо) успешно создан:",
            result
          );
        } else {
          throw new Error(
            "Выберите организационно-правовую форму: ТОО, ИП или Физическое лицо на ГПХ"
          );
        }

        // Добавляем банковские счета отдельным эндпоинтом
        if (result?.id && bankAccounts.length > 0) {
          for (const account of bankAccounts) {
            const bankName = account.bankName?.trim();
            const iik = account.accountNumber?.trim();
            const bik = account.bik?.trim();
            const currency = account.currency?.trim();
            if (bankName && iik && bik && currency) {
              await addCounterpartyBankAccount(result.id, {
                bank_name: bankName,
                bik,
                iik,
                currency,
                is_primary: !!account.isMain,
                is_active: true,
              });
            }
          }
        }
      }

      // После успешного сохранения показываем попап и затем редиректим обратно
      setShowSuccessPopup(true);
      setTimeout(() => {
        setShowSuccessPopup(false);
        navigate("/projects/counterparties");
      }, 2000);
    } catch (error) {
      console.error("Ошибка при создании контрагента:", error);
      setSubmitError(
        error.message || "Не удалось создать контрагента. Попробуйте еще раз."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate("/projects/counterparties");
  };

  const handleDelete = async () => {
    if (!isEditMode || !id) return;

    const entityName = isOrganization ? "организацию" : "контрагента";
    const confirmed = window.confirm(
      `Вы уверены, что хотите удалить эту ${entityName}? Это действие нельзя отменить.`
    );

    if (!confirmed) return;

    try {
      setIsSubmitting(true);
      setSubmitError(null);

      await deleteCounterparty(id);
      console.log(isOrganization ? "Организация успешно удалена" : "Контрагент успешно удален");

      navigate("/projects/counterparties");
    } catch (error) {
      console.error(`Ошибка при удалении ${entityName}:`, error);
      setSubmitError(
        error.message || `Не удалось удалить ${entityName}. Попробуйте еще раз.`
      );
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="add-counterparty-container">
        <div className="add-counterparty-loading">Загрузка данных...</div>
      </div>
    );
  }

  return (
    <div className="add-counterparty-container">
      <h1 className="add-counterparty-title">
        {isEditMode
          ? isOrganization
            ? "Редактировать организацию"
            : "Редактировать контрагента"
          : isOrganization
          ? "Добавить организацию"
          : "Добавить контрагента"}
      </h1>

      <form className="add-counterparty-form" onSubmit={handleSubmit}>
        {/* Выбор типа контрагента */}
        <div className="add-counterparty-form-section">
          <h2 className="add-counterparty-form-section-title">
            Выберите тип контрагента
          </h2>
          <div className="add-counterparty-radio-group">
            <label className="add-counterparty-radio-label">
              <input
                type="radio"
                name="counterpartyType"
                value="counterparty"
                checked={counterpartyType === "counterparty"}
                onChange={(e) => setCounterpartyType(e.target.value)}
              />
              <span className="add-counterparty-radio-custom"></span>
              Контрагент
            </label>
            <label className="add-counterparty-radio-label">
              <input
                type="radio"
                name="counterpartyType"
                value="organization"
                checked={counterpartyType === "organization"}
                onChange={(e) => setCounterpartyType(e.target.value)}
              />
              <span className="add-counterparty-radio-custom"></span>
              Наша компания
            </label>
          </div>
        </div>

        {/* Основная информация */}
        <div className="add-counterparty-form-section">
          <h2 className="add-counterparty-form-section-title">
            Основная информация
          </h2>
          <div className="add-counterparty-form-grid">
            <div className="add-counterparty-form-group">
              <label>
                Организационно-правовая форма{" "}
                <span className="add-counterparty-required">*</span>
              </label>
              <Select
                value={legalForm}
                onChange={setLegalForm}
                options={legalFormOptions}
                placeholder="Выберите ОПФ"
                styles={selectStyles}
                classNamePrefix="select"
              />
              {errors.legalForm && (
                <span className="add-counterparty-error-message">
                  {errors.legalForm}
                </span>
              )}
            </div>

            <div className="add-counterparty-form-group">
              <label>
                БИН/ИИН <span className="add-counterparty-required">*</span>
              </label>
              <div className="add-counterparty-input-with-icon">
                <input
                  type="text"
                  value={binIin}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, "");
                    if (value.length <= 12) {
                      setBinIin(value);
                    }
                  }}
                  placeholder="Введите 12 цифр"
                  maxLength={12}
                />
                {binIin.length === 12 && (
                  <svg
                    className="add-counterparty-check-icon"
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                  >
                    <path
                      d="M16.6667 5L7.50004 14.1667L3.33337 10"
                      stroke="#2a5e38"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </div>
              {errors.binIin && (
                <span className="add-counterparty-error-message">
                  {errors.binIin}
                </span>
              )}
            </div>

            {isIndividualPerson ? (
              <div className="add-counterparty-form-group add-counterparty-form-group-full">
                <label>
                  ФИО <span className="add-counterparty-required">*</span>
                </label>
                <div className="add-counterparty-input-with-icon">
                  <input
                    type="text"
                    value={personFullName}
                    onChange={(e) => setPersonFullName(e.target.value)}
                    placeholder="Введите ФИО"
                  />
                  {personFullName && (
                    <svg
                      className="add-counterparty-check-icon"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                    >
                      <path
                        d="M16.6667 5L7.50004 14.1667L3.33337 10"
                        stroke="#2a5e38"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </div>
                {errors.personFullName && (
                  <span className="add-counterparty-error-message">
                    {errors.personFullName}
                  </span>
                )}
              </div>
            ) : (
              <>
                <div className="add-counterparty-form-group add-counterparty-form-group-full">
                  <label>
                    Полное наименование{" "}
                    <span className="add-counterparty-required">*</span>
                  </label>
                  <div className="add-counterparty-input-with-icon">
                    <input
                      type="text"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      placeholder='Введите полное наименование (например, Товарищество с ограниченной ответственностью "СтройПроект Астана")'
                    />
                    {fullName && (
                      <svg
                        className="add-counterparty-check-icon"
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                      >
                        <path
                          d="M16.6667 5L7.50004 14.1667L3.33337 10"
                          stroke="#2a5e38"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </div>
                  {errors.fullName && (
                    <span className="add-counterparty-error-message">
                      {errors.fullName}
                    </span>
                  )}
                </div>

                <div className="add-counterparty-form-group add-counterparty-form-group-full">
                  <label>
                    Сокращенное наименование{" "}
                    <span className="add-counterparty-required">*</span>
                  </label>
                  <div className="add-counterparty-input-with-icon">
                    <input
                      type="text"
                      value={shortName}
                      onChange={(e) => setShortName(e.target.value)}
                      placeholder="Введите сокращённое наименование"
                    />
                    {shortName && (
                      <svg
                        className="add-counterparty-check-icon"
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                      >
                        <path
                          d="M16.6667 5L7.50004 14.1667L3.33337 10"
                          stroke="#2a5e38"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </div>
                  {errors.shortName && (
                    <span className="add-counterparty-error-message">
                      {errors.shortName}
                    </span>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Документ, удостоверяющий личность - только для физических лиц */}
        {isIndividualPerson && (
          <div className="add-counterparty-form-section">
            <h2 className="add-counterparty-form-section-title">
              Документ, удостоверяющий личность
            </h2>
            <div className="add-counterparty-form-grid">
              <div className="add-counterparty-form-group">
                <label>
                  Тип документа{" "}
                  <span className="add-counterparty-required">*</span>
                </label>
                <Select
                  value={documentType}
                  onChange={setDocumentType}
                  options={documentTypeOptions}
                  placeholder="Выберите тип документа"
                  styles={selectStyles}
                  classNamePrefix="select"
                />
                {errors.documentType && (
                  <span className="add-counterparty-error-message">
                    {errors.documentType}
                  </span>
                )}
              </div>

              <div className="add-counterparty-form-group">
                <label>
                  Номер <span className="add-counterparty-required">*</span>
                </label>
                <div className="add-counterparty-input-with-icon">
                  <input
                    type="text"
                    value={documentNumber}
                    onChange={(e) => setDocumentNumber(e.target.value)}
                    placeholder="Введите номер документа"
                  />
                  {documentNumber && (
                    <svg
                      className="add-counterparty-check-icon"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                    >
                      <path
                        d="M16.6667 5L7.50004 14.1667L3.33337 10"
                        stroke="#2a5e38"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </div>
                {errors.documentNumber && (
                  <span className="add-counterparty-error-message">
                    {errors.documentNumber}
                  </span>
                )}
              </div>

              <div className="add-counterparty-form-group">
                <label>
                  Дата выдачи{" "}
                  <span className="add-counterparty-required">*</span>
                </label>
                <input
                  type="date"
                  value={documentIssueDate}
                  onChange={(e) => setDocumentIssueDate(e.target.value)}
                />
                {errors.documentIssueDate && (
                  <span className="add-counterparty-error-message">
                    {errors.documentIssueDate}
                  </span>
                )}
              </div>

              <div className="add-counterparty-form-group">
                <label>
                  Срок действия{" "}
                  <span className="add-counterparty-required">*</span>
                </label>
                <input
                  type="date"
                  value={documentExpirationDate}
                  onChange={(e) => setDocumentExpirationDate(e.target.value)}
                />
                {errors.documentExpirationDate && (
                  <span className="add-counterparty-error-message">
                    {errors.documentExpirationDate}
                  </span>
                )}
              </div>

              <div className="add-counterparty-form-group add-counterparty-form-group-full">
                <label>
                  Кем выдан <span className="add-counterparty-required">*</span>
                </label>
                <div className="add-counterparty-input-with-icon">
                  <input
                    type="text"
                    value={documentIssuedBy}
                    onChange={(e) => setDocumentIssuedBy(e.target.value)}
                    placeholder="Введите кем выдан"
                  />
                  {documentIssuedBy && (
                    <svg
                      className="add-counterparty-check-icon"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                    >
                      <path
                        d="M16.6667 5L7.50004 14.1667L3.33337 10"
                        stroke="#2a5e38"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </div>
                {errors.documentIssuedBy && (
                  <span className="add-counterparty-error-message">
                    {errors.documentIssuedBy}
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Адреса */}
        <div className="add-counterparty-form-section">
          <h2 className="add-counterparty-form-section-title">Адреса</h2>

          <div className="add-counterparty-address-subsection">
            <h3 className="add-counterparty-address-subsection-title">
              {isIndividualPerson ? "АДРЕС ПРОПИСКИ" : "ЮРИДИЧЕСКИЙ АДРЕС"}
            </h3>
            <div className="add-counterparty-form-grid">
              <div className="add-counterparty-form-group">
                <label>
                  Страна <span className="add-counterparty-required">*</span>
                </label>
                <Select
                  value={legalCountry}
                  onChange={setLegalCountry}
                  options={[{ value: "KZ", label: "Казахстан" }]}
                  styles={selectStyles}
                  classNamePrefix="select"
                />
              </div>

              {isIndividualPerson ? (
                <>
                  <div className="add-counterparty-form-group">
                    <label>
                      Почтовый индекс{" "}
                      <span className="add-counterparty-required">*</span>
                    </label>
                    <div className="add-counterparty-input-with-icon">
                      <input
                        type="text"
                        value={legalPostalCode}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, "");
                          setLegalPostalCode(value);
                        }}
                        placeholder="Введите почтовый индекс"
                        maxLength={6}
                      />
                      {legalPostalCode && (
                        <svg
                          className="add-counterparty-check-icon"
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                        >
                          <path
                            d="M16.6667 5L7.50004 14.1667L3.33337 10"
                            stroke="#2a5e38"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </div>
                  </div>

                  <div className="add-counterparty-form-group">
                    <label>
                      Город / населённый пункт{" "}
                      <span className="add-counterparty-required">*</span>
                    </label>
                    <Select
                      value={legalCity}
                      onChange={setLegalCity}
                      options={cityOptions}
                      placeholder="Выберите город / населённый пункт"
                      styles={selectStyles}
                      classNamePrefix="select"
                    />
                  </div>

                  <div className="add-counterparty-form-group add-counterparty-form-group-full">
                    <label>
                      Улица / район{" "}
                      <span className="add-counterparty-required">*</span>
                    </label>
                    <div className="add-counterparty-input-with-icon">
                      <input
                        type="text"
                        value={legalStreet}
                        onChange={(e) => setLegalStreet(e.target.value)}
                        placeholder="Введите улицу / район"
                      />
                      {legalStreet && (
                        <svg
                          className="add-counterparty-check-icon"
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                        >
                          <path
                            d="M16.6667 5L7.50004 14.1667L3.33337 10"
                            stroke="#2a5e38"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </div>
                  </div>

                  <div className="add-counterparty-form-group">
                    <label>
                      Номер здания{" "}
                      <span className="add-counterparty-required">*</span>
                    </label>
                    <div className="add-counterparty-input-with-icon">
                      <input
                        type="text"
                        value={legalBuilding}
                        onChange={(e) => setLegalBuilding(e.target.value)}
                        placeholder="Введите дом / корпус"
                      />
                      {legalBuilding && (
                        <svg
                          className="add-counterparty-check-icon"
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                        >
                          <path
                            d="M16.6667 5L7.50004 14.1667L3.33337 10"
                            stroke="#2a5e38"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="add-counterparty-form-group">
                    <label>Город / населённый пункт</label>
                    <Select
                      value={legalCity}
                      onChange={setLegalCity}
                      options={cityOptions}
                      placeholder="Выберите город / населённый пункт"
                      styles={selectStyles}
                      classNamePrefix="select"
                    />
                  </div>

                  <div className="add-counterparty-form-group">
                    <label>Номер здания</label>
                    <div className="add-counterparty-input-with-icon">
                      <input
                        type="text"
                        value={legalBuilding}
                        onChange={(e) => setLegalBuilding(e.target.value)}
                        placeholder="Введите дом / корпус"
                      />
                      {legalBuilding && (
                        <svg
                          className="add-counterparty-check-icon"
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                        >
                          <path
                            d="M16.6667 5L7.50004 14.1667L3.33337 10"
                            stroke="#2a5e38"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </div>
                  </div>

                  <div className="add-counterparty-form-group">
                    <label>Почтовый индекс</label>
                    <div className="add-counterparty-input-with-icon">
                      <input
                        type="text"
                        value={legalPostalCode}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, "");
                          setLegalPostalCode(value);
                        }}
                        placeholder="Введите почтовый индекс"
                        maxLength={6}
                      />
                      {legalPostalCode && (
                        <svg
                          className="add-counterparty-check-icon"
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                        >
                          <path
                            d="M16.6667 5L7.50004 14.1667L3.33337 10"
                            stroke="#2a5e38"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </div>
                  </div>

                  <div className="add-counterparty-form-group add-counterparty-form-group-full">
                    <label>Улица / район</label>
                    <div className="add-counterparty-input-with-icon">
                      <input
                        type="text"
                        value={legalStreet}
                        onChange={(e) => setLegalStreet(e.target.value)}
                        placeholder="Введите улицу / район"
                      />
                      {legalStreet && (
                        <svg
                          className="add-counterparty-check-icon"
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                        >
                          <path
                            d="M16.6667 5L7.50004 14.1667L3.33337 10"
                            stroke="#2a5e38"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="add-counterparty-address-subsection">
            <h3 className="add-counterparty-address-subsection-title">
              ФАКТИЧЕСКИЙ АДРЕС
            </h3>
            <div className="add-counterparty-checkbox-group">
              <label className="add-counterparty-checkbox-label">
                <input
                  type="checkbox"
                  checked={actualAddressMatches}
                  onChange={(e) => handleActualAddressToggle(e.target.checked)}
                />
                <span className="add-counterparty-checkbox-custom"></span>
                {isIndividualPerson
                  ? "Совпадает с адресом прописки"
                  : "Совпадает с юридическим адресом"}
              </label>
            </div>

            {!actualAddressMatches && (
              <div className="add-counterparty-form-grid">
                <div className="add-counterparty-form-group">
                  <label>Страна</label>
                  <Select
                    value={actualCountry}
                    onChange={setActualCountry}
                    options={[{ value: "KZ", label: "Казахстан" }]}
                    styles={selectStyles}
                    classNamePrefix="select"
                  />
                </div>

                <div className="add-counterparty-form-group">
                  <label>Город / населённый пункт</label>
                  <Select
                    value={actualCity}
                    onChange={setActualCity}
                    options={cityOptions}
                    placeholder="Выберите город / населённый пункт"
                    styles={selectStyles}
                    classNamePrefix="select"
                  />
                </div>

                <div className="add-counterparty-form-group">
                  <label>Номер здания</label>
                  <div className="add-counterparty-input-with-icon">
                    <input
                      type="text"
                      value={actualBuilding}
                      onChange={(e) => setActualBuilding(e.target.value)}
                      placeholder="Введите дом / корпус"
                    />
                    {actualBuilding && (
                      <svg
                        className="add-counterparty-check-icon"
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                      >
                        <path
                          d="M16.6667 5L7.50004 14.1667L3.33337 10"
                          stroke="#2a5e38"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </div>
                </div>

                <div className="add-counterparty-form-group">
                  <label>Почтовый индекс</label>
                  <div className="add-counterparty-input-with-icon">
                    <input
                      type="text"
                      value={actualPostalCode}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, "");
                        setActualPostalCode(value);
                      }}
                      placeholder="Введите почтовый индекс"
                      maxLength={6}
                    />
                    {actualPostalCode && (
                      <svg
                        className="add-counterparty-check-icon"
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                      >
                        <path
                          d="M16.6667 5L7.50004 14.1667L3.33337 10"
                          stroke="#2a5e38"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </div>
                </div>

                <div className="add-counterparty-form-group add-counterparty-form-group-full">
                  <label>Улица / район</label>
                  <div className="add-counterparty-input-with-icon">
                    <input
                      type="text"
                      value={actualStreet}
                      onChange={(e) => setActualStreet(e.target.value)}
                      placeholder="Введите улицу / район"
                    />
                    {actualStreet && (
                      <svg
                        className="add-counterparty-check-icon"
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                      >
                        <path
                          d="M16.6667 5L7.50004 14.1667L3.33337 10"
                          stroke="#2a5e38"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Контактная информация / Руководство и общие контакты */}
        {isIndividualPerson ? (
          <div className="add-counterparty-form-section">
            <h2 className="add-counterparty-form-section-title">
              Контактная информация
            </h2>
            <div className="add-counterparty-form-grid">
              <div className="add-counterparty-form-group">
                <label>
                  Телефон <span className="add-counterparty-required">*</span>
                </label>
                <div className="add-counterparty-input-with-icon">
                  <input
                    type="tel"
                    value={personPhone}
                    onChange={(e) => setPersonPhone(e.target.value)}
                    placeholder="+7..."
                  />
                  {personPhone && (
                    <svg
                      className="add-counterparty-check-icon"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                    >
                      <path
                        d="M16.6667 5L7.50004 14.1667L3.33337 10"
                        stroke="#2a5e38"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </div>
                {errors.personPhone && (
                  <span className="add-counterparty-error-message">
                    {errors.personPhone}
                  </span>
                )}
              </div>

              <div className="add-counterparty-form-group">
                <label>
                  E-mail <span className="add-counterparty-required">*</span>
                </label>
                <div className="add-counterparty-input-with-icon">
                  <input
                    type="email"
                    value={personEmail}
                    onChange={(e) => setPersonEmail(e.target.value)}
                    placeholder="Введите E-mail"
                  />
                  {personEmail && (
                    <svg
                      className="add-counterparty-check-icon"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                    >
                      <path
                        d="M16.6667 5L7.50004 14.1667L3.33337 10"
                        stroke="#2a5e38"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </div>
                {errors.personEmail && (
                  <span className="add-counterparty-error-message">
                    {errors.personEmail}
                  </span>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="add-counterparty-form-section">
            <h2 className="add-counterparty-form-section-title">
              Руководство и общие контакты
            </h2>
            <div className="add-counterparty-form-grid">
              <div className="add-counterparty-form-group add-counterparty-form-group-full">
                <label>Полное ФИО первого руководителя</label>
                <div className="add-counterparty-input-with-icon">
                  <input
                    type="text"
                    value={directorFullName}
                    onChange={(e) => setDirectorFullName(e.target.value)}
                    placeholder="Введите ФИО первого руководителя"
                  />
                  {directorFullName && (
                    <svg
                      className="add-counterparty-check-icon"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                    >
                      <path
                        d="M16.6667 5L7.50004 14.1667L3.33337 10"
                        stroke="#2a5e38"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </div>
              </div>

              <div className="add-counterparty-form-group">
                <label>Должность</label>
                <div className="add-counterparty-input-with-icon">
                  <input
                    type="text"
                    value={directorPosition}
                    onChange={(e) => setDirectorPosition(e.target.value)}
                    placeholder="Введите должность"
                  />
                  {directorPosition && (
                    <svg
                      className="add-counterparty-check-icon"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                    >
                      <path
                        d="M16.6667 5L7.50004 14.1667L3.33337 10"
                        stroke="#2a5e38"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </div>
              </div>

              <div className="add-counterparty-form-group">
                <label>Телефон организации</label>
                <div className="add-counterparty-input-with-icon">
                  <input
                    type="tel"
                    value={organizationPhone}
                    onChange={(e) => setOrganizationPhone(e.target.value)}
                    placeholder="+7..."
                  />
                  {organizationPhone && (
                    <svg
                      className="add-counterparty-check-icon"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                    >
                      <path
                        d="M16.6667 5L7.50004 14.1667L3.33337 10"
                        stroke="#2a5e38"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </div>
              </div>

              <div className="add-counterparty-form-group">
                <label>E-mail организации</label>
                <div className="add-counterparty-input-with-icon">
                  <input
                    type="email"
                    value={organizationEmail}
                    onChange={(e) => setOrganizationEmail(e.target.value)}
                    placeholder="Введите E-mail"
                  />
                  {organizationEmail && (
                    <svg
                      className="add-counterparty-check-icon"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                    >
                      <path
                        d="M16.6667 5L7.50004 14.1667L3.33337 10"
                        stroke="#2a5e38"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </div>
              </div>

              <div className="add-counterparty-form-group">
                <label>Сайт</label>
                <div className="add-counterparty-input-with-icon">
                  <input
                    type="text"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                    placeholder="www.example.kz"
                  />
                  {website && (
                    <svg
                      className="add-counterparty-check-icon"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                    >
                      <path
                        d="M16.6667 5L7.50004 14.1667L3.33337 10"
                        stroke="#2a5e38"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Контактные лица - только для организаций */}
        {!isIndividualPerson && (
          <div className="add-counterparty-form-section">
            <div className="add-counterparty-form-section-header">
              <h2 className="add-counterparty-form-section-title">
                Контактные лица
              </h2>
              <button
                type="button"
                className="add-counterparty-add-item-btn"
                onClick={handleAddContactPerson}
              >
                + Добавить контакт
              </button>
            </div>

            {contactPersons.map((person) => (
              <div
                key={person.id}
                className="add-counterparty-contact-person-item"
              >
                <button
                  type="button"
                  className="add-counterparty-remove-item-btn"
                  onClick={() => handleRemoveContactPerson(person.id)}
                >
                  ×
                </button>
                <div className="add-counterparty-form-grid">
                  <div className="add-counterparty-form-group">
                    <label>ФИО</label>
                    <input
                      type="text"
                      value={person.fullName}
                      onChange={(e) =>
                        handleContactPersonChange(
                          person.id,
                          "fullName",
                          e.target.value
                        )
                      }
                      placeholder="Введите ФИО"
                    />
                  </div>
                  <div className="add-counterparty-form-group">
                    <label>Должность</label>
                    <input
                      type="text"
                      value={person.position}
                      onChange={(e) =>
                        handleContactPersonChange(
                          person.id,
                          "position",
                          e.target.value
                        )
                      }
                      placeholder="Введите должность"
                    />
                  </div>
                  <div className="add-counterparty-form-group">
                    <label>Телефон</label>
                    <input
                      type="tel"
                      value={person.phone}
                      onChange={(e) =>
                        handleContactPersonChange(
                          person.id,
                          "phone",
                          e.target.value
                        )
                      }
                      placeholder="+7..."
                    />
                  </div>
                  <div className="add-counterparty-form-group">
                    <label>E-mail</label>
                    <input
                      type="email"
                      value={person.email}
                      onChange={(e) =>
                        handleContactPersonChange(
                          person.id,
                          "email",
                          e.target.value
                        )
                      }
                      placeholder="Введите E-mail"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Лицензии - только для организаций */}
        {!isIndividualPerson && (
          <div className="add-counterparty-form-section">
            <h2 className="add-counterparty-form-section-title">Лицензии</h2>
            <div className="add-counterparty-checkbox-group">
              <label className="add-counterparty-checkbox-label">
                <input
                  type="checkbox"
                  checked={hasLicenses}
                  onChange={(e) => setHasLicenses(e.target.checked)}
                />
                <span className="add-counterparty-checkbox-custom"></span>
                Есть лицензируемые виды деятельности
              </label>
            </div>

            {hasLicenses && (
              <div className="add-counterparty-form-grid">
                <div className="add-counterparty-form-group">
                  <label>Номер лицензии</label>
                  <input
                    type="text"
                    value={licenseNumber}
                    onChange={(e) => setLicenseNumber(e.target.value)}
                    placeholder="Введите номер лицензии"
                  />
                </div>
                <div className="add-counterparty-form-group">
                  <label>Дата выдачи</label>
                  <input
                    type="date"
                    value={licenseIssueDate}
                    onChange={(e) => setLicenseIssueDate(e.target.value)}
                  />
                </div>
                <div className="add-counterparty-form-group">
                  <label>Дата окончания</label>
                  <input
                    type="date"
                    value={licenseExpirationDate}
                    onChange={(e) => setLicenseExpirationDate(e.target.value)}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Налоги */}
        <div className="add-counterparty-form-section">
          <h2 className="add-counterparty-form-section-title">Налоги</h2>
          <div className="add-counterparty-form-grid">
            <div className="add-counterparty-form-group">
              <label>Режим налогообложения</label>
              <Select
                value={taxRegime}
                onChange={setTaxRegime}
                options={taxRegimeOptions}
                placeholder="Выберите режим налогообложения"
                styles={selectStyles}
                classNamePrefix="select"
              />
            </div>

            <div className="add-counterparty-form-group">
              <label>Плательщик НДС</label>
              <div className="add-counterparty-radio-group">
                <label className="add-counterparty-radio-label">
                  <input
                    type="radio"
                    name="isNdsPayer"
                    value="true"
                    checked={isNdsPayer === true}
                    onChange={() => setIsNdsPayer(true)}
                  />
                  <span className="add-counterparty-radio-custom"></span>
                  Да
                </label>
                <label className="add-counterparty-radio-label">
                  <input
                    type="radio"
                    name="isNdsPayer"
                    value="false"
                    checked={isNdsPayer === false}
                    onChange={() => setIsNdsPayer(false)}
                  />
                  <span className="add-counterparty-radio-custom"></span>
                  Нет
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Банковские счета */}
        <div className="add-counterparty-form-section">
          <div className="add-counterparty-form-section-header">
            <h2 className="add-counterparty-form-section-title">
              Банковские счета
            </h2>
            <button
              type="button"
              className="add-counterparty-add-item-btn"
              onClick={handleAddBankAccount}
            >
              + Добавить счет
            </button>
          </div>

          {bankAccounts.map((account) => (
            <div
              key={account.id}
              className="add-counterparty-bank-account-item"
            >
              <button
                type="button"
                className="add-counterparty-remove-item-btn"
                onClick={() => handleRemoveBankAccount(account.id)}
              >
                ×
              </button>
              <div className="add-counterparty-form-grid">
                <div className="add-counterparty-form-group">
                  <label>
                    Банк <span className="add-counterparty-required">*</span>
                  </label>
                  <input
                    type="text"
                    value={account.bankName}
                    onChange={(e) =>
                      handleBankAccountChange(
                        account.id,
                        "bankName",
                        e.target.value
                      )
                    }
                    placeholder="Введите наименование банка"
                  />
                </div>
                <div className="add-counterparty-form-group">
                  <label>
                    ИИК <span className="add-counterparty-required">*</span>
                  </label>
                  <input
                    type="text"
                    value={account.accountNumber}
                    onChange={(e) =>
                      handleBankAccountChange(
                        account.id,
                        "accountNumber",
                        e.target.value
                      )
                    }
                    placeholder="Введите ИИК"
                  />
                </div>
                <div className="add-counterparty-form-group">
                  <label>
                    БИК <span className="add-counterparty-required">*</span>
                  </label>
                  <input
                    type="text"
                    value={account.bik}
                    onChange={(e) =>
                      handleBankAccountChange(account.id, "bik", e.target.value)
                    }
                    placeholder="Введите БИК"
                  />
                </div>
                <div className="add-counterparty-form-group">
                  <label>
                    Валюта <span className="add-counterparty-required">*</span>
                  </label>
                  <input
                    type="text"
                    value={account.currency}
                    onChange={(e) =>
                      handleBankAccountChange(
                        account.id,
                        "currency",
                        e.target.value
                      )
                    }
                    placeholder="Выберите валюту"
                  />
                </div>
              </div>
              {bankAccounts.length > 1 && (
                <div className="add-counterparty-bank-account-main-checkbox">
                  <label className="add-counterparty-checkbox-label">
                    <input
                      type="checkbox"
                      checked={account.isMain || false}
                      onChange={(e) =>
                        handleMainAccountChange(account.id, e.target.checked)
                      }
                    />
                    <span className="add-counterparty-checkbox-custom"></span>
                    Основной счет
                  </label>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Сообщение об ошибке */}
        {submitError && (
          <div className="add-counterparty-submit-error">{submitError}</div>
        )}

        {/* Кнопки */}
        <div className="add-counterparty-form-actions">
          <div className="add-counterparty-form-actions-left">
            {isEditMode && (
              <button
                type="button"
                className="add-counterparty-delete-btn"
                onClick={handleDelete}
                disabled={isSubmitting}
              >
                {isOrganization ? "Удалить организацию" : "Удалить контрагента"}
              </button>
            )}
          </div>
          <div className="add-counterparty-form-actions-right">
            <button
              type="button"
              className="add-counterparty-cancel-btn"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Отмена
            </button>
            <button
              type="submit"
              className="add-counterparty-submit-btn"
              disabled={isSubmitting}
            >
              {isSubmitting
                ? "Сохранение..."
                : isEditMode
                ? "Сохранить изменения"
                : isOrganization
                ? "Добавить организацию"
                : "Добавить контрагента"}
            </button>
          </div>
        </div>
      </form>

      {showSuccessPopup && (
        <div className="popup-overlay">
          <div className="popup">
            <div className="popup-icon"></div>
            <p className="popup-text">
              {isEditMode
                ? isOrganization
                  ? "Организация успешно обновлена"
                  : "Контрагент успешно обновлен"
                : isOrganization
                ? "Организация успешно создана"
                : "Контрагент успешно создан"}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddCounterparty;
